import { useCallback, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';
import type { UseFormReturn } from 'react-hook-form';
import toast from 'react-hot-toast';

import type { ContactShowSchema } from '@liscio/api';
import { useApiClient, useTrackEvent } from '@liscio/common';

import { applyCreateContactApiErrors } from './applyCreateContactApiErrors';
import type { CreateContactFormValues } from './createContactValidationSchema';
import {
  mapCreateContactFormValuesToApiPayload,
  type CreateContactPayload,
} from './mapCreateContactFormValuesToApiPayload';
import { toErrorObjectArray } from 'fetch-utils/api-errors';
import { useHomeData } from 'fetch-utils/users/user-hooks';

const CREATE_FIRM_CONTACT_URL = '/api/v5/firms/contacts';

const jsonRequestHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
} as const;

export type UseCreateContactSubmitOptions = {
  source?: string;
  accountId?: string | null;
  onSuccess?: (
    contact: ContactShowSchema & { invitation_status?: string }
  ) => void;
};

export function useCreateContactSubmit(
  formMethods: UseFormReturn<CreateContactFormValues>,
  options?: UseCreateContactSubmitOptions
) {
  const { client } = useApiClient();
  const { handleSubmit, setError } = formMethods;
  const { trackEvent } = useTrackEvent();
  const { data: homeData } = useHomeData();

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const homeDataRef = useRef(homeData);
  homeDataRef.current = homeData;

  const { mutate, isLoading } = useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const { data } = await client.post<
        ContactShowSchema & { invitation_status?: string }
      >(CREATE_FIRM_CONTACT_URL, payload, { headers: jsonRequestHeaders });
      return data;
    },
    onSuccess: (contact) => {
      const { source, accountId } = optionsRef.current ?? {};
      if (source) {
        trackEventRef.current('contact_created', {
          source,
          ...(source === 'onboarding'
            ? { onboarding_step: 'contact_created' }
            : {}),
          user_role: 'firm_employee',
          company_name: homeDataRef.current?.data?.userpilot_data?.firm_name,
          contact_id: contact.id,
          linked_to_account: Boolean(accountId),
        });
      }
      optionsRef.current?.onSuccess?.(contact);
    },
    onError: (error) => {
      const apiError = toErrorObjectArray(error);
      const message = applyCreateContactApiErrors(apiError, setError);
      toast.error(message);
    },
  });

  const submitCreateContact = useCallback(
    () =>
      handleSubmit((values) => {
        mutate(
          mapCreateContactFormValuesToApiPayload(
            values,
            optionsRef.current?.accountId
          )
        );
      })(),
    [handleSubmit, mutate]
  );

  return {
    submitCreateContact,
    isSubmitting: isLoading,
  };
}
